import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getTeams as fetchTeams } from "../api/teamApi";
import { getTeamMembers as fetchTeamMembers } from "../api/teamMemberApi";

/** @type {any} */
const defaultTeamContextValue = {
  teamId: null,
  setTeamId: (..._args) => {},
  teamName: null,
  setTeamName: (..._args) => {},
  organizationName: null,
  setOrganizationName: (..._args) => {},
  organizationId: null,
  setOrganizationId: (..._args) => {},
  teams: [],
  setTeams: (..._args) => {},
  teamMembers: [],
  setTeamMembers: (..._args) => {},
  teamMoods: [],
  setTeamMoods: (..._args) => {},
  teamCauses: [],
  setTeamCauses: (..._args) => {},
  activeTeamCauseTypes: [],
  setActiveTeamCauseTypes: (..._args) => {},
  isLead: false,
  setIsLead: (..._args) => {},
};

const TeamContext = createContext(defaultTeamContextValue);

export const TeamProvider = ({ children }) => {
  const [teamId, setTeamId] = useState(null);
  const [teamName, setTeamName] = useState(null);
  const [organizationName, setOrganizationName] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMoods, setTeamMoods] = useState([]);
  const [teamCauses, setTeamCauses] = useState([]);
  const [activeTeamCauseTypes, setActiveTeamCauseTypes] = useState([]);
  const [isLead, setIsLead] = useState(false);

  // Teams and team members are the parts of the old Firestore model that
  // experience-backend actually supports today (GET /team, GET /teammember),
  // so fetch them for real instead of stubbing.
  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    fetchTeams(organizationId)
      .then((data) => {
        if (!cancelled) setTeams(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.warn("Failed to load teams from experience-backend:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    fetchTeamMembers(teamId)
      .then((data) => {
        if (!cancelled) setTeamMembers(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.warn(
          "Failed to load team members from experience-backend:",
          error
        );
      });
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const value = useMemo(
    () => ({
      teamId,
      setTeamId,
      teamName,
      setTeamName,
      organizationName,
      setOrganizationName,
      organizationId,
      setOrganizationId,
      teams,
      setTeams,
      teamMembers,
      setTeamMembers,
      teamMoods,
      setTeamMoods,
      teamCauses,
      setTeamCauses,
      activeTeamCauseTypes,
      setActiveTeamCauseTypes,
      isLead,
      setIsLead,
    }),
    [
      teamId,
      teamName,
      organizationName,
      organizationId,
      teams,
      teamMembers,
      teamMoods,
      teamCauses,
      activeTeamCauseTypes,
      isLead,
    ]
  );

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
};

export const useTeam = () => useContext(TeamContext);
